import React, { useState } from "react";
import {
  CheckCircle2, Wrench, Cog, XCircle, Trash2, RotateCcw,
  HelpCircle, Send, Inbox, Truck, Paperclip, FileBadge, StickyNote, Undo2,
} from "lucide-react";
import { Modal } from "./Modal";
import { SubcontractorTypeahead } from "./SubcontractorTypeahead";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

/* ===== Field types supported by ActionModal =====
 * text | number | textarea | select | select-other | yesno | scrap-reason
 * Optional: required, options, default, showIf(vals) -> bool
 */

const SCRAP_REASONS = [
  "Beyond economical repair", "Out of tolerance — unrepairable",
  "Physical damage", "Obsolete / parts unavailable",
  "Customer requested disposal",
];

const GRID_ROWS = 10, GRID_COLS = 10;
const makeGrid = () => Array.from({ length: GRID_ROWS }, () => Array.from({ length: GRID_COLS }, () => ""));
const colLabel = (i) => String.fromCharCode(65 + i); // A..J

// Resolve an A1-style cell reference to its numeric value (recursively evaluates
// referenced formula cells, with a depth guard to avoid cycles).
function cellRefValue(ref, grid, depth) {
  const m = /^([A-J])(\d{1,2})$/i.exec(ref);
  if (!m) return 0;
  const c = m[1].toUpperCase().charCodeAt(0) - 65;
  const r = parseInt(m[2], 10) - 1;
  if (r < 0 || r >= GRID_ROWS || c < 0 || c >= GRID_COLS) return 0;
  let v = (grid[r] && grid[r][c]) != null ? grid[r][c] : "";
  v = String(v).trim();
  if (v.startsWith("=")) {
    if (depth > 20) return 0;
    const res = evalFormula(v.slice(1), grid, depth + 1);
    return res == null ? 0 : res;
  }
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

// Safely evaluate a spreadsheet-style arithmetic expression (+ - * / and parens),
// resolving any A1-style cell references first. Returns a number or null.
function evalFormula(expr, grid, depth = 0) {
  const substituted = String(expr).replace(/[A-J]\d{1,2}/gi,
    (ref) => `(${cellRefValue(ref, grid, depth)})`);
  const clean = substituted.replace(/[^0-9+\-*/().\s]/g, "");
  if (!clean.trim()) return null;
  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict";return (${clean})`)();
    if (typeof result === "number" && isFinite(result)) {
      return Math.round(result * 1e9) / 1e9; // tame float noise
    }
  } catch (e) { /* invalid expression — leave as typed */ }
  return null;
}

function ReadingsGrid({ value, onChange, action }) {
  const grid = Array.isArray(value) && value.length ? value : makeGrid();
  const cell = (r, c) => (grid[r] && grid[r][c] != null ? grid[r][c] : "");
  const writeCell = (r, c, v) => {
    const next = makeGrid().map((row, ri) => row.map((_, ci) => cell(ri, ci)));
    next[r][c] = v;
    onChange(next);
  };
  // On Enter / blur, if the cell holds an =formula, replace it with the result.
  const commit = (r, c) => {
    const raw = String(cell(r, c)).trim();
    if (!raw.startsWith("=")) return;
    const res = evalFormula(raw.slice(1), grid);
    if (res != null) writeCell(r, c, String(res));
  };
  return (
    <div className="readings-grid-wrap" data-testid={`action-${action}-readings`}>
      <table className="readings-grid">
        <thead>
          <tr>
            <th className="rg-corner"></th>
            {Array.from({ length: GRID_COLS }).map((_, c) => <th key={c}>{colLabel(c)}</th>)}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: GRID_ROWS }).map((_, r) => (
            <tr key={r}>
              <th className="rg-rownum">{r + 1}</th>
              {Array.from({ length: GRID_COLS }).map((_, c) => (
                <td key={c}>
                  <input value={cell(r, c)} onChange={(e) => writeCell(r, c, e.target.value)}
                    onBlur={() => commit(r, c)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(r, c); } }}
                    data-testid={`reading-${r}-${c}`} spellCheck={false} autoComplete="off" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="t-mut" style={{ fontSize: 11, padding: "5px 8px" }}>
        Tip: start a cell with <code>=</code> to calculate — e.g. <code>=100/2+5</code> or
        <code> =A1+B1*2</code> — then press Enter (or click out) to replace it with the result.
      </div>
    </div>
  );
}

export const ACTIONS = {
  calibrated: {
    label: "Mark Calibrated", icon: CheckCircle2, tone: "gold", status: "calibrated",
    intro: "Records that lab work is done. The Issue Certificate button will appear next — click it to mint the CR number.",
    fields: [
      { k: "calibration_result", l: "Result", type: "select", options: ["passed", "adjusted", "repaired"], default: "passed" },
      { k: "repair_time_minutes", l: "Time in lab (min)", type: "number" },
      { k: "calibration_readings", l: "Calibration recordings", type: "readings-grid",
        hint: "Capture your lab readings (numbers or letters). Saved to the equipment's service history when the certificate is issued." },
      { k: "technician_notes", l: "Technician notes", type: "textarea" },
    ],
  },
  adjusted: {
    label: "Mark Adjusted", icon: Cog, tone: "gold", status: "adjusted",
    intro: "Equipment was adjusted to bring it back into tolerance. The Issue Certificate button will appear next.",
    fields: [
      { k: "repair_time_minutes", l: "Time in lab (min)", type: "number" },
      { k: "replaced_with", l: "What was adjusted / replaced", type: "text" },
      { k: "calibration_readings", l: "Calibration recordings", type: "readings-grid",
        hint: "Capture your lab readings (numbers or letters). Saved to the equipment's service history when the certificate is issued." },
      { k: "technician_notes", l: "Technician notes", type: "textarea" },
    ],
  },
  repaired: {
    label: "Mark Repaired", icon: Wrench, tone: "gold", status: "repaired",
    intro: "Equipment was repaired and is back in spec. The Issue Certificate button will appear next.",
    fields: [
      { k: "repair_time_minutes", l: "Repair time (min)", type: "number" },
      { k: "fuses_used", l: "Fuses used", type: "text" },
      { k: "batteries_used", l: "Batteries used", type: "text" },
      { k: "replaced_with", l: "Parts replaced", type: "text" },
      { k: "calibration_readings", l: "Calibration recordings", type: "readings-grid",
        hint: "Capture your lab readings (numbers or letters). Saved to the equipment's service history when the certificate is issued." },
      { k: "technician_notes", l: "Technician notes", type: "textarea" },
    ],
  },

  // ---------- Explicit Certificate issuance (after work is finished) ----------
  issue_certificate: {
    label: "Issue Certificate", icon: FileBadge, tone: "gold",
    intro: "Mints the next CR number (CRYY-NNNNN, sequential per year), creates the calibration certificate and stamps the equipment record with the new due date.",
    customSubmit: async ({ item, vals }) => {
      const body = {};
      if (vals.calibration_result) body.calibration_result = vals.calibration_result;
      if (vals.notes && vals.notes.trim()) body.notes = vals.notes.trim();
      const res = await api.post(`/job-items/${item.id}/issue-certificate`, body);
      const cert = res.data?.certificate_number;
      if (cert) toast.success(`Certificate issued: ${cert}`, { duration: 8000 });
    },
    fields: [
      { k: "calibration_result", l: "Calibration result", type: "select",
        options: ["passed", "adjusted", "repaired"], default: "passed" },
      { k: "notes", l: "Notes (optional)", type: "textarea",
        placeholder: "Anything to record alongside the certificate" },
    ],
  },
  failed_calibration: {
    label: "Mark Failed", icon: XCircle, tone: "danger", status: "failed_calibration",
    intro: "Equipment failed calibration — out of tolerance and not adjusted/repaired.",
    fields: [{ k: "technician_notes", l: "Reason / readings", type: "textarea", required: true }],
  },
  unable_to_calibrate: {
    label: "Unable to Calibrate", icon: HelpCircle, tone: "danger", status: "unable_to_calibrate",
    intro: "Cannot be calibrated (e.g. missing accessories, faulty before tolerance can be assessed).",
    fields: [{ k: "technician_notes", l: "Why", type: "textarea", required: true }],
  },
  returned_without_work: {
    label: "Returned (No Work)", icon: RotateCcw, tone: "ghost", status: "returned_without_work",
    intro: "No work performed (customer pulled, wrong item, out of scope).",
    fields: [{ k: "technician_notes", l: "Reason", type: "textarea", required: true }],
  },

  // ---------- Enhanced Scrap workflow ----------
  scrapped: {
    label: "Mark Unserviceable", icon: Trash2, tone: "danger",
    intro: "Equipment will be marked Unserviceable, removed from future calibration/recall reports, and shown to the customer as Unserviceable. Admin will be notified. Required fields are marked *.",
    customSubmit: async ({ item, vals, otherText, file }) => {
      const reason = vals._reason === "Other…" ? otherText.trim() : vals._reason;
      if (!reason) throw new Error("Reason is required");
      if (!vals.fault_description?.trim()) throw new Error("Fault description is required");
      const body = {
        reason,
        fault_description: vals.fault_description,
        replacement_required: vals.replacement_required === "yes",
        replacement_recommendation: vals.replacement_required === "yes" ? (vals.replacement_recommendation || null) : null,
        customer_approval_required: vals.customer_approval_required === "yes",
        customer_facing_notes: vals.customer_facing_notes || null,
        internal_notes: vals.internal_notes || null,
      };
      if (!item.equipment_id) throw new Error("This job-line isn't linked to an equipment record yet — open the equipment record to scrap it.");
      await api.post(`/equipment/${item.equipment_id}/scrap`, body);
      // also flip the job-item status (audit-tracked)
      await api.patch(`/job-items/${item.id}/status`, {
        status: "scrapped", technician_notes: vals.internal_notes || null,
      });
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/job-items/${item.id}/attachments`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      }
    },
    fields: [
      { k: "_reason", l: "Scrap reason", type: "scrap-reason", required: true },
      { k: "fault_description", l: "Fault description", type: "textarea", required: true,
        placeholder: "What's actually broken? Be specific — this is the technician's verdict that admin will reference." },
      { k: "replacement_required", l: "Replacement required?", type: "yesno", default: "no" },
      { k: "replacement_recommendation", l: "Replacement recommendation", type: "text",
        showIf: (v) => v.replacement_required === "yes",
        placeholder: "Model / part to quote for the customer" },
      { k: "customer_approval_required", l: "Customer approval required before disposal?", type: "yesno", default: "no" },
      { k: "customer_facing_notes", l: "Customer-facing notes (shown on portal)", type: "textarea",
        placeholder: "What the customer should see on their portal alongside 'Unserviceable'." },
      { k: "internal_notes", l: "Internal notes (staff only)", type: "textarea" },
    ],
  },

  // ---------- Enhanced Parts Request ----------
  parts_required: {
    label: "Order a part", icon: Cog, tone: "ghost",
    intro: "Logs a parts/spares order and moves this item to 'Awaiting Spares'. The Manager (and Parts) are notified to place the order and update the status; you'll be alerted when it's received.",
    customSubmit: async ({ item, vals, file }) => {
      if (!vals.part_description?.trim()) throw new Error("Part description is required");
      if (!vals.reason_required?.trim()) throw new Error("Reason required is required");
      // Optional file → upload to job_item first, capture the attachment id
      let photoId = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const att = await api.post(`/job-items/${item.id}/attachments`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        photoId = att.data?.id || null;
      }
      await api.post("/parts", {
        job_item_id: item.id,
        part_description: vals.part_description,
        part_number: vals.part_number || null,
        quantity: Number(vals.quantity) || 1,
        reason_required: vals.reason_required,
        supplier_suggestion: vals.supplier_suggestion || null,
        can_work_continue: vals.can_work_continue === "yes",
        customer_approval_required: vals.customer_approval_required === "yes",
        is_repair_item: vals.is_repair_item === "yes",
        notes: vals.notes || null,
        photo_attachment_id: photoId,
      });
    },
    fields: [
      { k: "part_description", l: "Part description", type: "text", required: true,
        placeholder: "e.g. Replacement encoder PCB" },
      { k: "part_number", l: "Part number (if known)", type: "text" },
      { k: "quantity", l: "Quantity", type: "number", default: 1 },
      { k: "reason_required", l: "Reason required", type: "textarea", required: true,
        placeholder: "Why is this part needed? What failed / what's missing?" },
      { k: "supplier_suggestion", l: "Suggested supplier (optional)", type: "text",
        placeholder: "e.g. Mitutoyo SA, RS Components" },
      { k: "can_work_continue", l: "Can work continue without this part?", type: "yesno", default: "no" },
      { k: "customer_approval_required", l: "Customer approval required before ordering?", type: "yesno", default: "no" },
      { k: "is_repair_item", l: "Is this a repair item?", type: "yesno", default: "yes" },
      { k: "notes", l: "Notes for parts / admin", type: "textarea" },
    ],
  },

  awaiting_customer_approval: {
    label: "Request Customer Approval", icon: HelpCircle, tone: "ghost",
    status: "awaiting_customer_approval",
    intro: "Customer needs to confirm something (e.g. repair cost over threshold) before work continues.",
    fields: [{ k: "customer_question", l: "What do we need them to approve?", type: "textarea", required: true }],
  },
  awaiting_subcontractor: {
    label: "Send to Subcontractor", icon: Send, tone: "ghost",
    status: "awaiting_subcontractor",
    intro: "Booking with an external lab — item leaves your lab.",
    fields: [
      { k: "subcontractor_name", l: "Subcontractor", type: "subcontractor", required: true },
      { k: "technician_notes", l: "What we need them to do", type: "textarea" },
    ],
  },
  ready_for_admin: {
    label: "Send to Admin", icon: Inbox, tone: "gold", status: "ready_for_admin",
    intro: "Hands the item off to admin (paperwork, invoicing, customer comms).",
    fields: [{ k: "technician_notes", l: "Hand-off notes", type: "textarea" }],
  },
  ready_for_delivery: {
    label: "In Dispatch", icon: Truck, tone: "gold", status: "ready_for_delivery",
    intro: "Item is packed and ready to leave the lab.",
    fields: [{ k: "technician_notes", l: "Dispatch notes", type: "textarea" }],
  },

  // ---------- Return to Customer (customer decided not to continue) ----------
  return_to_customer: {
    label: "Return to Customer (RtC)", icon: Undo2, tone: "gold",
    intro: "The customer has decided not to continue with this job — it is NOT unserviceable or defective. The item moves straight to Admin and Admin is flagged immediately to arrange the return.",
    customSubmit: async ({ item, vals }) => {
      await api.post(`/job-items/${item.id}/return-to-customer`, { reason: vals.reason || null });
    },
    fields: [
      { k: "reason", l: "Reason / notes (optional)", type: "textarea",
        placeholder: "e.g. Customer decided not to proceed with the repair" },
    ],
  },

  // ---------- Quick technician note (no status change) ----------
  add_note: {
    label: "Add Note", icon: StickyNote, tone: "ghost",
    intro: "Add a technician note to this line. It's appended to any existing notes — the status is not changed.",
    customSubmit: async ({ item, vals }) => {
      const note = (vals.note || "").trim();
      if (!note) throw new Error("Note is required");
      const existing = (item.technician_notes || "").trim();
      const combined = existing ? `${existing}\n${note}` : note;
      await api.patch(`/job-items/${item.id}`, { technician_notes: combined });
    },
    fields: [
      { k: "note", l: "Note", type: "textarea", required: true,
        placeholder: "What do you want to record on this line?" },
    ],
  },
};

function FieldRow({ field, value, onChange, otherText, setOtherText, action }) {
  const f = field;
  const td = `action-${action}-${f.k}`;
  if (f.type === "textarea") {
    return <textarea className="input" rows={3} value={value ?? ""} placeholder={f.placeholder}
      onChange={(e) => onChange(e.target.value)} data-testid={td} />;
  }
  if (f.type === "select") {
    return <select className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={td}>
      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>;
  }
  if (f.type === "yesno") {
    return (
      <div style={{ display: "inline-flex", gap: 6 }}>
        {["yes", "no"].map((y) => (
          <button key={y} type="button"
            className={`btn sm ${value === y ? (y === "yes" ? "gold" : "ghost") : "ghost"}`}
            style={value === y ? {} : { opacity: 0.55 }}
            onClick={() => onChange(y)} data-testid={`${td}-${y}`}>
            {y.toUpperCase()}
          </button>
        ))}
      </div>
    );
  }
  if (f.type === "readings-grid") {
    return <ReadingsGrid value={value} onChange={onChange} action={action} />;
  }
  if (f.type === "subcontractor") {
    return <SubcontractorTypeahead value={value ?? ""} onChange={onChange}
      placeholder="Start typing — pick from history or enter new" testid={td} />;
  }
  if (f.type === "scrap-reason") {
    return (
      <>
        <select className="input" value={value ?? SCRAP_REASONS[0]} onChange={(e) => onChange(e.target.value)} data-testid={td}>
          {[...SCRAP_REASONS, "Other…"].map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {value === "Other…" && (
          <textarea className="input" style={{ marginTop: 8 }} rows={2} value={otherText}
            onChange={(e) => setOtherText(e.target.value)} placeholder="Describe the reason…"
            data-testid={`${td}-other`} />
        )}
      </>
    );
  }
  return <input className="input" type={f.type === "number" ? "number" : "text"}
    value={value ?? ""} placeholder={f.placeholder}
    onChange={(e) => onChange(e.target.value)} data-testid={td} />;
}

export function ActionModal({ action, item, onClose, onDone }) {
  const spec = ACTIONS[action];
  const [vals, setVals] = useState(() => {
    const v = {};
    (spec?.fields || []).forEach((f) => {
      v[f.k] = f.type === "readings-grid" ? makeGrid() : (f.default ?? "");
    });
    return v;
  });
  const [otherText, setOtherText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!spec) return null;

  const setV = (k, v) => setVals((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      if (spec.customSubmit) {
        await spec.customSubmit({ item, vals, otherText, file });
      } else {
        // validate required for default flow
        for (const f of spec.fields) {
          if (!f.required) continue;
          const v = vals[f.k];
          if (!v || (typeof v === "string" && !v.trim())) {
            throw new Error(`${f.l} is required`);
          }
        }
        const body = { status: spec.status };
        for (const f of spec.fields) {
          let v = vals[f.k];
          if (f.type === "number") v = v === "" || v == null ? null : Number(v);
          if (v !== "" && v != null) body[f.k] = v;
        }
        await api.patch(`/job-items/${item.id}/status`, body);
        if (file) {
          const fd = new FormData(); fd.append("file", file);
          await api.post(`/job-items/${item.id}/attachments`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
        }
      }
      toast.success(`${spec.label} — saved`);
      onDone && onDone();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.message?.includes("required") ? e.message : apiErr(e));
    } finally { setBusy(false); }
  };

  const visibleFields = spec.fields.filter((f) => !f.showIf || f.showIf(vals));
  const Icon = spec.icon;
  const hasGrid = spec.fields.some((f) => f.type === "readings-grid");

  return (
    <Modal open onClose={onClose} icon={Icon} title={spec.label} widest={hasGrid}
      subtitle={`${item.description || ""}${item.serial_number ? " · SN " + item.serial_number : ""}`}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className={`btn ${spec.tone === "danger" ? "danger" : spec.tone === "ghost" ? "ghost" : "gold"}`}
            onClick={submit} disabled={busy} data-testid={`action-confirm-${action}`}>
            {busy ? "Saving…" : "Confirm"}
          </button>
        </>
      }>
      {spec.intro && <div className="notice" style={{ marginBottom: 14 }}><Icon size={16} /><span>{spec.intro}</span></div>}
      <div className="form-grid">
        {visibleFields.map((f) => (
          <div className="fld" key={f.k}
            style={{ gridColumn: ["textarea", "scrap-reason", "readings-grid"].includes(f.type) ? "span 2" : undefined }}>
            <label>{f.l} {f.required && <span className="req">*</span>}</label>
            <FieldRow field={f} value={vals[f.k]} onChange={(v) => setV(f.k, v)}
              otherText={otherText} setOtherText={setOtherText} action={action} />
            {f.hint && <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>{f.hint}</div>}
          </div>
        ))}
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label><Paperclip size={11} style={{ verticalAlign: "middle", marginRight: 4 }} /> Attach photo / file (optional)</label>
          <input type="file" className="input" onChange={(e) => setFile(e.target.files?.[0] || null)}
            accept="image/*,.pdf" data-testid={`action-${action}-file`} />
          {file && <div className="t-mut" style={{ fontSize: 12, marginTop: 4 }}>{file.name} · {Math.round(file.size / 1024)} KB</div>}
        </div>
      </div>
    </Modal>
  );
}
